import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type GoogleMapReact from 'google-map-react';

import type { SidebarPopulationMode } from './interface';

export const insertInitialSidebarStation = ({
    station,
    initialSidebarOrderRef,
    setBaseSidebarStations,
    sideListLimit,
}: {
    station: any;
    initialSidebarOrderRef: MutableRefObject<Array<string | number>>;
    setBaseSidebarStations: Dispatch<SetStateAction<any[]>>;
    sideListLimit: number;
}) => {
    const stationId = station.properties?.id;
    if (stationId === undefined || stationId === null) return;

    setBaseSidebarStations((currentStations) => {
        const nextStations = currentStations.filter(
            (currentStation) => currentStation.properties?.id !== stationId
        );

        nextStations.push(station);

        return nextStations
            .sort(
                (leftStation, rightStation) =>
                    initialSidebarOrderRef.current.indexOf(leftStation.properties?.id) -
                    initialSidebarOrderRef.current.indexOf(rightStation.properties?.id)
            )
            .slice(0, sideListLimit);
    });
};

export const promoteSidebarStation = ({
    station,
    setSelectedSidebarStation,
}: {
    station: any;
    setSelectedSidebarStation: Dispatch<SetStateAction<any | null>>;
}) => {
    const stationId = station.properties?.id;
    if (stationId === undefined || stationId === null) return;

    setSelectedSidebarStation(station);
};

export const refreshSidebarAroundStation = ({
    station,
    stations,
    refreshSidebarStations,
}: {
    station: any;
    stations: any[];
    refreshSidebarStations: (
        nextStations: any[],
        mode: Exclude<SidebarPopulationMode, 'preserve'>,
        referencePoint: GoogleMapReact.Coords,
        options?: { preserveActiveStation?: boolean }
    ) => void;
}) => {
    const [lng, lat] = station.geometry?.coordinates ?? [];

    if (typeof lat !== 'number' || typeof lng !== 'number') {
        return;
    }

    refreshSidebarStations(stations, 'station-proximity', {lat, lng}, {
        preserveActiveStation: true,
    });
};

export const preserveSummaryStationStatus = (summaryStation: any, detailedStation: any) => {
    const summaryStatus = summaryStation?.properties?.data?.status;

    if (!summaryStatus || !detailedStation?.properties?.data) {
        return detailedStation;
    }

    return {
        ...detailedStation,
        properties: {
            ...detailedStation.properties,
            data: {
                ...detailedStation.properties.data,
                status: summaryStatus,
            },
        },
    };
};

export const refreshSidebarStations = ({
    nextStations,
    mode,
    referencePoint,
    options,
    getNearestStations,
    sideListLimit,
    userLocation,
    geolocationFallbackCenter,
    sidebarInitializedRef,
    sidebarPopulationModeRef,
    initialSidebarOrderRef,
    sidebarBatchIdRef,
    setStationDetailsById,
    setStationDetailsLoadingById,
    stationDetailsByIdRef,
    stationDetailsLoadingByIdRef,
    sidebarBatchStartedAtRef,
    sidebarDetailsDurationRef,
    sidebarDetailsCompletedCountRef,
    sidebarDetailsTargetCountRef,
    setShowLocationSearchEmptyState,
    setBaseSidebarStations,
    setSelectedSidebarStation,
    setExpandedSidebarStationId,
    setOpenMarker,
    clearRoute,
    loadStationDetails,
    insertInitialSidebarStation,
}: {
    nextStations: any[];
    mode: Exclude<SidebarPopulationMode, 'preserve'>;
    referencePoint: GoogleMapReact.Coords;
    options?: { preserveActiveStation?: boolean };
    getNearestStations: (
        sourceStations: any[],
        referencePoint: GoogleMapReact.Coords,
        limit: number
    ) => any[];
    sideListLimit: number;
    userLocation: GoogleMapReact.Coords | null;
    geolocationFallbackCenter: GoogleMapReact.Coords;
    sidebarInitializedRef: MutableRefObject<boolean>;
    sidebarPopulationModeRef: MutableRefObject<SidebarPopulationMode>;
    initialSidebarOrderRef: MutableRefObject<Array<string | number>>;
    sidebarBatchIdRef: MutableRefObject<number>;
    setStationDetailsById: Dispatch<SetStateAction<Record<string, any>>>;
    setStationDetailsLoadingById: Dispatch<SetStateAction<Record<string, boolean>>>;
    stationDetailsByIdRef: MutableRefObject<Record<string, any>>;
    stationDetailsLoadingByIdRef: MutableRefObject<Record<string, boolean>>;
    sidebarBatchStartedAtRef: MutableRefObject<number | null>;
    sidebarDetailsDurationRef: MutableRefObject<number>;
    sidebarDetailsCompletedCountRef: MutableRefObject<number>;
    sidebarDetailsTargetCountRef: MutableRefObject<number>;
    setShowLocationSearchEmptyState: Dispatch<SetStateAction<boolean>>;
    setBaseSidebarStations: Dispatch<SetStateAction<any[]>>;
    setSelectedSidebarStation: Dispatch<SetStateAction<any | null>>;
    setExpandedSidebarStationId: Dispatch<SetStateAction<string | number | null>>;
    setOpenMarker: Dispatch<SetStateAction<string | number | null>>;
    clearRoute: () => void;
    loadStationDetails: (stationId: string | number, batchId: number, summaryStation?: any) => Promise<any>;
    insertInitialSidebarStation: (station: any) => void;
}) => {
    const sidebarStations =
        mode === 'cluster-order'
            ? getNearestStations(nextStations, referencePoint, sideListLimit)
            : mode === 'station-proximity'
                ? getNearestStations(nextStations, referencePoint, sideListLimit)
                : getNearestStations(nextStations, userLocation ?? geolocationFallbackCenter, sideListLimit);

    sidebarInitializedRef.current = true;
    sidebarPopulationModeRef.current = mode;
    initialSidebarOrderRef.current = sidebarStations.map((station) => station.properties?.id);
    sidebarBatchIdRef.current += 1;
    setStationDetailsById({});
    setStationDetailsLoadingById({});
    stationDetailsByIdRef.current = {};
    stationDetailsLoadingByIdRef.current = {};
    sidebarBatchStartedAtRef.current = performance.now();
    sidebarDetailsDurationRef.current = 0;
    sidebarDetailsCompletedCountRef.current = 0;
    sidebarDetailsTargetCountRef.current = sidebarStations.length;
    setShowLocationSearchEmptyState(false);
    setBaseSidebarStations([]);

    if (!options?.preserveActiveStation) {
        setSelectedSidebarStation(null);
        setExpandedSidebarStationId(null);
        setOpenMarker(null);
        clearRoute();
    }

    sidebarStations.forEach((station) => {
        const stationId = station.properties?.id;
        if (stationId !== undefined && stationId !== null) {
            void loadStationDetails(stationId, sidebarBatchIdRef.current, station).then((detailedStation) => {
                if (detailedStation) {
                    insertInitialSidebarStation(detailedStation);
                }
            });
        }
    });
};
